// Helper function to normalize text
const normalizeText = (text) => {
  return text
    .toLowerCase()
    .replace(/[-_\s]/g, "") // Remove spaces, dashes, underscores
    .replace(/[^a-z0-9]/g, ""); // Remove any other special characters
};

// Calculate cosine similarity between two strings
const calculateCosineSimilarity = (str1, str2) => {
  const text1 = normalizeText(str1);
  const text2 = normalizeText(str2);

  // Create character frequency maps
  const freqMap1 = {};
  const freqMap2 = {};

  // Fill frequency maps
  for (let char of text1) {
    freqMap1[char] = (freqMap1[char] || 0) + 1;
  }
  for (let char of text2) {
    freqMap2[char] = (freqMap2[char] || 0) + 1;
  }

  // Get all unique characters
  const uniqueChars = new Set([
    ...Object.keys(freqMap1),
    ...Object.keys(freqMap2),
  ]);

  // Calculate dot product and magnitudes
  let dotProduct = 0;
  let magnitude1 = 0;
  let magnitude2 = 0;

  for (let char of uniqueChars) {
    const freq1 = freqMap1[char] || 0;
    const freq2 = freqMap2[char] || 0;
    dotProduct += freq1 * freq2;
    magnitude1 += freq1 * freq1;
    magnitude2 += freq2 * freq2;
  }

  // Calculate cosine similarity
  const similarity =
    dotProduct / (Math.sqrt(magnitude1) * Math.sqrt(magnitude2));
  return similarity || 0;
};

// Calculate Levenshtein distance ratio
const calculateLevenshteinRatio = (str1, str2) => {
  const text1 = normalizeText(str1);
  const text2 = normalizeText(str2);

  const matrix = Array(text2.length + 1)
    .fill(null)
    .map(() => Array(text1.length + 1).fill(null));

  for (let i = 0; i <= text1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= text2.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= text2.length; j++) {
    for (let i = 1; i <= text1.length; i++) {
      const indicator = text1[i - 1] === text2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }

  const distance = matrix[text2.length][text1.length];
  const maxLength = Math.max(text1.length, text2.length);
  return (maxLength - distance) / maxLength;
};

// Calculate substring similarity
const calculateSubstringScore = (str1, str2) => {
  const text1 = normalizeText(str1);
  const text2 = normalizeText(str2);

  // Check if one is substring of another
  if (text1.includes(text2) || text2.includes(text1)) {
    const lengthRatio =
      Math.min(text1.length, text2.length) /
      Math.max(text1.length, text2.length);
    return 0.8 + 0.2 * lengthRatio; // Base score of 0.8 plus length ratio bonus
  }
  return 0;
};

// Calculate overall similarity score with weighted components
const calculateSimilarityScore = (required, header, columnMappings) => {
  const baseScore = calculateCosineSimilarity(required, header);
  const levenshteinScore = calculateLevenshteinRatio(required, header);
  const substringScore = calculateSubstringScore(required, header);

  // Enhanced semantic scoring
  let semanticScore = 0;
  if (columnMappings && columnMappings[required]) {
    const variations = columnMappings[required];
    const normalizedHeader = normalizeText(header);

    // Check for partial matches and word similarity
    const headerParts = normalizedHeader.split(/[^a-z0-9]+/);
    variations.forEach((variation) => {
      const variationParts = normalizeText(variation).split(/[^a-z0-9]+/);

      // Score part matches
      headerParts.forEach((headerPart) => {
        if (headerPart.length < 3) return; // Skip very short parts

        variationParts.forEach((variationPart) => {
          if (variationPart.length < 3) return; // Skip very short parts

          // Exact part match
          if (headerPart === variationPart) {
            semanticScore = Math.max(semanticScore, 0.9);
          }
          // Contained part match
          else if (
            headerPart.includes(variationPart) ||
            variationPart.includes(headerPart)
          ) {
            semanticScore = Math.max(semanticScore, 0.8);
          }
          // High similarity part match
          else {
            const partSimilarity = calculateCosineSimilarity(
              headerPart,
              variationPart
            );
            if (partSimilarity > 0.8) {
              semanticScore = Math.max(semanticScore, 0.7);
            }
          }
        });
      });
    });
  }

  // Calculate final score using a weighted max approach
  const scores = [
    { score: semanticScore, weight: 1.0 },
    { score: substringScore, weight: 0.9 },
    { score: baseScore, weight: 0.8 },
    { score: levenshteinScore, weight: 0.7 },
  ];

  // Take weighted maximum of all scores
  const maxScore = Math.max(...scores.map((s) => s.score * s.weight));

  // Boost score if multiple metrics show good matches
  const goodScores = scores.filter((s) => s.score > 0.7).length;
  const boost = goodScores > 1 ? 0.1 : 0;

  return Math.min(1, maxScore + boost);
};

// Find best matches for each required field
export const findBestMatches = (
  headers,
  requiredFields,
  columnMappings = {}
) => {
  const suggestions = {};
  const usedHeaders = new Set();
  const matchScores = {};

  // Calculate scores for all possible combinations
  requiredFields.forEach((required) => {
    matchScores[required] = {};
    headers.forEach((header) => {
      const score = calculateSimilarityScore(required, header, columnMappings);
      if (score > 0.6) {
        // Only consider matches above threshold
        matchScores[required][header] = score;
      }
    });
  });

  // Sort required fields by their best match score (highest first)
  const sortedFields = requiredFields.sort((a, b) => {
    const aMaxScore = Math.max(...Object.values(matchScores[a] || {}), 0);
    const bMaxScore = Math.max(...Object.values(matchScores[b] || {}), 0);
    return bMaxScore - aMaxScore;
  });

  // Assign best matches, ensuring no header is used twice
  sortedFields.forEach((required) => {
    const headerScores = matchScores[required] || {};
    const availableHeaders = Object.entries(headerScores)
      .filter(([header]) => !usedHeaders.has(header))
      .sort(([, scoreA], [, scoreB]) => scoreB - scoreA);

    if (availableHeaders.length > 0) {
      const [bestHeader] = availableHeaders[0];
      suggestions[required] = bestHeader;
      usedHeaders.add(bestHeader);
    } else {
      suggestions[required] = null;
    }
  });

  // Return unmapped headers as well
  const unmappedColumns = headers.filter((header) => !usedHeaders.has(header));

  return {
    suggestions,
    unmappedColumns,
  };
};
